import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Header from "../components/Header";
import RatingsAggregate from "../components/RatingsAggregate";
import { api, formatApiErrorDetail } from "../lib/api";

export default function EmployerPublicProfile() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/employers/${id}/profile`);
        setOrg(data);
      } catch (err) {
        setError(
          formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  const logoSrc = useMemo(() => {
    if (!org?.logo_url) return null;
    if (org.logo_url.startsWith("http")) return org.logo_url;
    return `${process.env.REACT_APP_BACKEND_URL}${org.logo_url}`;
  }, [org?.logo_url]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employer-public-profile-page">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-10">
        {loading && <div className="text-center text-slate-500">{t("common.loading")}</div>}
        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm" data-testid="employer-public-error">
            <p className="text-sm text-rose-700">{error}</p>
            <Link to="/dashboard" className="mt-4 inline-block text-sm font-medium text-indigo-700 hover:text-indigo-800">
              ← {t("common.back")}
            </Link>
          </div>
        )}
        {!loading && !error && org && (
          <div className="space-y-6">
            <section className="flex flex-wrap items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                {logoSrc ? (
                  <img src={logoSrc} alt="Organization logo" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-slate-300">
                    {(org.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-[240px]">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl" data-testid="employer-public-name">
                  {org.name}
                </h1>
                {org.website && (
                  <a href={org.website} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm text-indigo-700 hover:text-indigo-800" data-testid="employer-public-website">
                    {org.website}
                  </a>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  {org.industry && (
                    <span className="rounded-md bg-slate-100 px-2 py-0.5" data-testid="employer-public-industry">{org.industry}</span>
                  )}
                  {org.size && (
                    <span className="rounded-md bg-slate-100 px-2 py-0.5" data-testid="employer-public-size">
                      {t("employer_public.size_prefix")} {org.size}
                    </span>
                  )}
                </div>
              </div>
            </section>

            <section className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:col-span-2">
                <h2 className="text-base font-semibold text-slate-900 md:text-lg">
                  {t("employer_public.about_title")}
                </h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="employer-public-description">
                  {org.description || t("employer_public.no_description")}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("employer_public.ratings_title")}
                </p>
                <div className="mt-3" data-testid="employer-public-ratings">
                  <RatingsAggregate
                    summary={org.rating_summary}
                    dimensions={[
                      { key: "communication", label: t("rating.communication") },
                      { key: "responsiveness", label: t("rating.responsiveness") },
                      { key: "payment_timeliness", label: t("rating.payment_timeliness") },
                    ]}
                    emptyState={t("employer_public.ratings_empty")}
                  />
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
